// components/ui/language-switcher.tsx
"use client";

import { useIsArabic } from "@/hooks/use-isArabic";
import { LanguageType } from "@/lib/types";
import { _t, cn } from "@/lib/utils";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation"; // ✅ 1. Import useRouter
import { useState } from "react";
import LoaderDots from "../loaders/loading-dots";

export default function LanguageSwitcher({
  border = true,
  classNames,
}: {
  border?: boolean;
  classNames?: string;
}) {
  const pathname = usePathname();
  const router = useRouter(); // ✅ 2. Initialize the router
  const [loading, setLoading] = useState(false);
  
  const { lang: currentLanguage } = useIsArabic();
  // Determine the next language for the loading message
  const nextLang = currentLanguage === LanguageType.EN ? LanguageType.AR : LanguageType.EN;

  const toggleLanguage = () => {
    setLoading(true);

    // Determine the new language prefix
    const newLanguagePrefix =
      currentLanguage === LanguageType.EN
        ? LanguageType.AR.toLowerCase()
        : LanguageType.EN.toLowerCase();

    // Create the new path
    const newPath = pathname.replace(
      /^\/(en-us|ar-sa)/,
      `/${newLanguagePrefix}`
    );
    
    // ✅ 3. Preserve existing URL search parameters (e.g., ?s=filter)
    const finalUrl = newPath + window.location.search;

    setTimeout(() => {
      // ✅ 4. Use router.push for a smooth, client-side navigation
      router.push(finalUrl);
    }, 1000);
  };

  return (
    <>
      <div className="inline-flex items-center px-1">
        <Link
          href="#"
          onClick={(e) => {
            e.preventDefault();
            toggleLanguage();
          }}
          className={cn(
            border && "border dark:border-inputBorderDark dark:bg-black",
            "px-3 py-1 rounded-sm under text-sm max-h-[34px]",
            classNames
          )}
        >
          <div className="flex h-6 items-center gap-x-2">
            {currentLanguage === LanguageType.EN ? (
              <div className="flex items-center gap-x-2">
                ع{" "}
                <Image
                  priority
                  className="max-w-7"
                  src="/images/flags/saudi-flag.jpg"
                  width={100}
                  height={67}
                  alt="Saudi Flag"
                  unoptimized
                />
              </div>
            ) : (
              <div className="flex items-center gap-x-2">
                En{" "}
                <Image
                  priority
                  className="max-w-7"
                  src="/images/flags/uk-flag.jpg"
                  width={100}
                  height={67}
                  alt="UK Flag"
                />
              </div>
            )}
          </div>
        </Link>
      </div>

      {loading && (
        <div className={cn("fixed inset-0 z-[9999] bg-black bg-opacity-70 flex flex-col justify-center items-center text-white text-xl font-medium space-y-6")}>
          <p className={currentLanguage === LanguageType.AR ? "rtl":"ltr"}>
            {_t("switching_language_to", nextLang)}{" "}
            {nextLang === LanguageType.AR ? _t("arabic",nextLang) : _t("english",nextLang)}
          </p>
          <LoaderDots />
        </div>
      )}
    </>
  );
}